import { create } from 'zustand';
import { api } from '../services/api';

interface InstagramAccount {
  id: string;
  username: string;
  accountType: string;
  mediaCount: number;
}

interface InstagramQuota {
  quotaUsage: number;
  quotaTotal: number;
  quotaDuration: number;
}

interface PublishResult {
  success: boolean;
  mediaId?: string;
  postId?: string;
  message?: string;
  error?: string;
}

interface PublishingState {
  // Instagram
  isInstagramConnected: boolean;
  instagramAccount: InstagramAccount | null;
  instagramQuota: InstagramQuota | null;
  isTestingInstagram: boolean;
  instagramError: string | null;

  // Publishing
  isPublishing: boolean;
  publishingPostId: string | null;
  publishingPlatform: string | null;
  publishResult: PublishResult | null;

  // Actions
  testInstagramConnection: () => Promise<void>;
  publishToInstagram: (postId: string) => Promise<PublishResult>;
  clearPublishResult: () => void;
}

export const usePublishingStore = create<PublishingState>((set) => ({
  // Initial state
  isInstagramConnected: false,
  instagramAccount: null,
  instagramQuota: null,
  isTestingInstagram: false,
  instagramError: null,

  isPublishing: false,
  publishingPostId: null,
  publishingPlatform: null,
  publishResult: null,

  // Test Instagram connection
  testInstagramConnection: async () => {
    set({ isTestingInstagram: true, instagramError: null });

    try {
      const result = await api.get<{
        success: boolean;
        account?: InstagramAccount;
        quota?: InstagramQuota;
        error?: string;
      }>('/instagram/test');

      if (result.success && result.account) {
        set({
          isInstagramConnected: true,
          instagramAccount: result.account,
          instagramQuota: result.quota || null,
          isTestingInstagram: false,
        });
      } else {
        set({
          isInstagramConnected: false,
          instagramAccount: null,
          instagramQuota: null,
          instagramError: result.error || 'Verbindung fehlgeschlagen',
          isTestingInstagram: false,
        });
      }
    } catch (error) {
      set({
        isInstagramConnected: false,
        instagramAccount: null,
        instagramQuota: null,
        instagramError: error instanceof Error ? error.message : 'Verbindung fehlgeschlagen',
        isTestingInstagram: false,
      });
    }
  },

  // Publish to Instagram
  publishToInstagram: async (postId: string) => {
    set({
      isPublishing: true,
      publishingPostId: postId,
      publishingPlatform: 'instagram',
      publishResult: null,
    });

    try {
      const result = await api.post<PublishResult>('/instagram/publish', {
        post_id: postId,
      });

      set({
        isPublishing: false,
        publishingPostId: null,
        publishingPlatform: null,
        publishResult: result,
      });

      return result;
    } catch (error) {
      const errorResult: PublishResult = {
        success: false,
        error: error instanceof Error ? error.message : 'Veröffentlichung fehlgeschlagen',
      };

      set({
        isPublishing: false,
        publishingPostId: null,
        publishingPlatform: null,
        publishResult: errorResult,
      });

      return errorResult;
    }
  },

  // Clear publish result
  clearPublishResult: () => {
    set({ publishResult: null });
  },
}));
